import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {Box} from '@mui/material';

import ArrSplitPanel from '../../common/arr-split-panel/ArrSplitPanel';

import SaveReportPanel from '../save-report-panel/SaveReportPanel';

import './StandardReports.scss';

import {ColumnConfig, Config, RightPanelConfig} from './config/reportTypes';

import {ISelectItem} from '../../../shared/types';

import StandardReportHeader from './StandardReportHeader';

import {FilterSection} from './component/left-panel/components/FilterSection';

import RightPanel from './component/right-panel/RightPanel';

import {SourceItem} from '../tally/selectSourceContainer/ArrSourceSelectedContainer';

import {ToastType} from '../../../shared/constants';

import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';

import {ReportGridProps} from './component/right-panel/ReportDataGrid';
import {ActionMenuPayloadType} from './reports/search-term/SearchTermReportContainer';
import {CustomMenuItem} from '../../common/arr-menu/ArrMenu';

type ResolvedReportParams = {
  fromDateTime?: any;
  toDateTime?: any;
  selectedReviewers?: any;
  sourceType?: any;
  sourceID?: any;
  [key: string]: any;
};

type StandardReportsProps = {
  title: string;
  reportsConfig: Config;
  showInfoIcon?: boolean;
  selectedOption: ISelectItem | null;
  rightHeaderActionOptions?: {label: string; id: string}[];
  onRightHeaderActionPick?: (value: {label: string; id: string}) => void;
  selectedRightHeaderAction?: {label: string; id: string} | null;
  optionsCaption?: string;
  onOptionChange?: (value: ISelectItem | null) => void;
  onAction?: (actionKey: string, values: Record<string, any>) => void;
  data: any;
  loading?: boolean;
  columns: ColumnConfig[];
  mapRows?: (data: any) => any[];
  rightPanelHeaderConfig?: RightPanelConfig;
  sources?: SourceItem[];
  initialSelectedSources?: Record<string, any>;
  disableSourceSelection?: boolean;
  saveReportConfig?: any;
  isNoData?: boolean;
  infoContent?: string[] | string;
  infoModalTitle?: string;
  getRowId?: (row: any) => string | number;
  onColumnToggleAction?: (columnId: string) => void; // Added
  onTileChange?: (key: string, selected: boolean) => void;
  totalSelectedCount?: number; // Added
  onFilterChange?: (values: Record<string, any>) => void;
  className?: string;
  customHeaderContent?: ReactElement | any;
  gridConfig?: ReportGridProps;
  isQueryRun?: boolean;
  enableSave?: boolean;
  validateActionFromParent?: boolean;
  isExportDataReport?: boolean;
  resolvedReportParams?: ResolvedReportParams;
  isExportDataReportShared?: boolean;
  rightContentComponent?: ReactElement;
  savedData?: any; // Optional prop for saved data
  actionMenuPayload?: ActionMenuPayloadType;
  fixedRightPanelWidth?: boolean;
  saveReportType?: 'default' | 'timelineGap';
  // To disable/enable continue update of values state variable base on reportConfig update in FilterSection
  observReportConfigUpdate?: boolean;
  selectedFilterValues?: Record<string, any>;
  resetDatePicker?: boolean;
  skipDateValidation?: boolean;
};

export const StandardReports: React.FC<StandardReportsProps> = ({
  title,
  reportsConfig,
  showInfoIcon,
  infoContent,
  infoModalTitle,
  selectedOption,
  onOptionChange,
  rightHeaderActionOptions,
  onRightHeaderActionPick,
  selectedRightHeaderAction,
  optionsCaption,
  onAction,
  data,
  loading,
  columns,
  mapRows,
  rightPanelHeaderConfig,
  sources = [],
  initialSelectedSources,
  disableSourceSelection = false,
  saveReportConfig,
  isNoData = true,
  getRowId = (row: any) => row.id ?? row.searchNumber,
  onColumnToggleAction,
  onTileChange,
  onFilterChange,
  className,
  customHeaderContent,
  gridConfig,
  isQueryRun,
  enableSave,
  validateActionFromParent = false,
  totalSelectedCount,
  isExportDataReport = false,
  resolvedReportParams,
  isExportDataReportShared = false,
  rightContentComponent,
  savedData,
  actionMenuPayload,
  fixedRightPanelWidth,
  saveReportType = 'default',
  observReportConfigUpdate = true,
  selectedFilterValues,
  resetDatePicker,
  skipDateValidation = false,
}) => {
  const [isSavePanelOpen, setIsSavePanelOpen] = useState(false);

  const [resetSignal, setResetSignal] = useState(false);

  const [showToast, setShowToast] = useState(false);

  const [toastMessage, setToastMessage] = useState('');

  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);

  const [runCompleted, setRunCompleted] = useState(false);

  const [exportRunCompleted, setExportRunCompleted] = useState(false);

  const [exportReportPayload, setExportReportPayload] = useState<any>(null);

  const [currentFilters, setCurrentFilters] = useState<Record<string, any>>({});

  const handleFilterAction = useCallback(
    (actionKey: string, values: Record<string, any>) => {
      onAction?.(actionKey, values);

      if (actionKey === 'reset') {
        setResetSignal(true);

        setTimeout(() => setResetSignal(false), 1000);

        setRunCompleted(false);
      } else if (actionKey === 'apply') {
        setRunCompleted(true);
      }
    },
    [onAction]
  );

  const handleFilterChange = (values: Record<string, any>) => {
    setCurrentFilters?.(values);
  };

  useEffect(() => {
    if (data) {
      setRunCompleted(true);
    }
  }, [data]);

  const canSave = enableSave ?? !!runCompleted;

  const handleFilterOnChange = useCallback(
    (values: Record<string, any>) => {
      onFilterChange?.(values);

      if (
        !isExportDataReport &&
        (!values || Object.keys(values).length === 0)
      ) {
        return;
      }

      if (isExportDataReport) {
        handleFilterChange(values);
      }
    },
    [isExportDataReport, onFilterChange]
  );

  const handleSavePanleValidation = useCallback(
    (isOpen: boolean) => {
      if (isExportDataReport) {
        if (!currentFilters || Object.keys(currentFilters).length === 0) {
          triggerToast('Please select source', [ToastType.Error]);
          return;
        }
      }
      setIsSavePanelOpen(isOpen);
    },
    [isExportDataReport, currentFilters]
  );
  // -------------

  const handleRightPanelAction = (action: CustomMenuItem) => {
    if (action.id === 'ExportData' && onAction) {
      onAction(action.id, {item: action, payload: actionMenuPayload});
    }
  };

  // -------------

  const leftContent = useMemo(
    () => (
      <FilterSection
        reportsConfig={reportsConfig}
        onAction={handleFilterAction}
        resetSignal={resetSignal}
        sources={sources}
        initialSelectedSources={initialSelectedSources}
        disableSourceSelection={disableSourceSelection}
        selectedOption={selectedOption}
        onChange={handleFilterOnChange}
        validateActionFromParent={validateActionFromParent}
        resolvedReportParams={resolvedReportParams}
        observReportConfigUpdate={observReportConfigUpdate}
        selectedFilterValues={selectedFilterValues}
        resetDatePicker={resetDatePicker}
        skipDateValidation={skipDateValidation}
      />
    ),
    [
      reportsConfig,
      handleFilterAction,
      resetSignal,
      sources,
      initialSelectedSources,
      disableSourceSelection,
      onFilterChange,
      isExportDataReport,
      currentFilters,
      selectedOption,
      validateActionFromParent,
      resolvedReportParams,
      selectedFilterValues,
      skipDateValidation,
    ]
  );

  const rightContent = useMemo(
    () => (
      <RightPanel
        isNoData={isNoData}
        isSaveReportInActive={!canSave}
        isSavePanelOpen={isSavePanelOpen}
        setIsSavePanelOpen={setIsSavePanelOpen}
        data={data}
        loading={loading}
        columns={columns}
        mapRows={mapRows}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        canSave={canSave}
        getRowId={getRowId}
        onColumnToggleAction={onColumnToggleAction} // Passed down
        customHeaderContent={customHeaderContent}
        gridConfig={gridConfig}
        isQueryRun={isQueryRun}
        onTileChange={onTileChange}
        totalSelectedCount={totalSelectedCount}
        showExportDataReportContent={isExportDataReport}
        filters={isExportDataReport ? currentFilters : {}}
        onRunStatusChange={(ran, savePayload) => {
          setExportRunCompleted(!!ran);

          setExportReportPayload(savePayload);
        }}
        isReportShared={isExportDataReportShared}
        rightContentComponent={rightContentComponent}
        savedData={savedData}
        actionMenuPayload={actionMenuPayload}
        onActionSelect={handleRightPanelAction}
        resetSignal={resetSignal}
      />
    ),
    [
      isNoData,
      isSavePanelOpen,
      data,
      loading,
      columns,
      mapRows,
      rightPanelHeaderConfig,
      canSave,
      onColumnToggleAction,
      isQueryRun,
      currentFilters,
      isExportDataReport,
      customHeaderContent,
      gridConfig,
      getRowId,
      onTileChange,
      totalSelectedCount,
      isExportDataReportShared,
      rightContentComponent,
      savedData,
      resetSignal,
    ]
  );

  const triggerToast = useCallback((message: string, status: ToastType[]) => {
    setToastMessage(message);

    setToastStatus(status);

    setShowToast(true);
  }, []);

  const onOptionChangeWrapper = (value: ISelectItem | null) => {
    setRunCompleted(false);

    onOptionChange?.(value);
  };

  return (
    <Box className={`review-results-container ${className ?? ''}`}>
      <StandardReportHeader
        title={title}
        showInfoIcon={showInfoIcon}
        infoContent={infoContent}
        infoModalTitle={infoModalTitle}
        selectedOption={selectedOption}
        onOptionChange={onOptionChangeWrapper}
        rightActionOptions={rightHeaderActionOptions}
        selectedRightAction={selectedRightHeaderAction}
        onRightActionPick={onRightHeaderActionPick}
        optionsCaption={optionsCaption}
        isExportDataReport={isExportDataReport}
        rightPanelHeaderConfig={rightPanelHeaderConfig}
        headerSave={{
          isOpen: isSavePanelOpen,
          toggle: handleSavePanleValidation,
          canSave: !!exportRunCompleted,
        }}
        isSaveReportInActive={!exportRunCompleted}
      />
      <ArrSplitPanel
        leftPanel={leftContent}
        rightPanel={rightContent}
        fixedRightPanelWidth={fixedRightPanelWidth}
      />
      <SaveReportPanel
        isOpen={isSavePanelOpen}
        onClose={() => setIsSavePanelOpen(false)}
        saveReportConfig={saveReportConfig}
        canSave={canSave}
        onSuccess={(action) => {
          triggerToast(
            action === 'overwrite'
              ? 'Report updated successfully'
              : 'Report saved successfully',
            [ToastType.Success]
          );

          setIsSavePanelOpen(false);
        }}
        onError={(error) => {
          if (error.response?.data?.title) {
            triggerToast(error.response?.data?.title, [ToastType.Error]);
          } else {
            triggerToast(error, [ToastType.Error]);
          }
        }}
        isExportDataReport={isExportDataReport}
        exportDataReportPayload={exportReportPayload}
        saveReportType={saveReportType}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default StandardReports;
